import { useContext } from 'react';

import useBuildAIConversationHistory from '@/components/AIChat/hooks/useBuildAIConversationHistory';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { useUserMetadata } from '@/hooks/api/useUserMetadata';
import useWorkflowEditorPageStore from '@/pages/WorkflowEditorPage/WorkflowEditorPage.store';

export const CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY = 'ci_config_expert_last_viewed_turn';

const useCIConfigExpertConversation = () => {
  // Optional on purpose: the header button mounts this on account-less pages as well.
  const { account } = useContext(GlobalPropsContext);
  const ciConfigSlug = useWorkflowEditorPageStore((s) => s.ciConfigSlug);
  const availability = useWorkflowEditorPageStore((s) => s.ciConfigExpertAvailability);
  const canChangeProjectSettings = useWorkflowEditorPageStore((s) => s.canChangeProjectSettings);
  const hasBeenOpened = useWorkflowEditorPageStore((s) => s.hasBeenOpened);
  const markAsViewed = useWorkflowEditorPageStore((s) => s.markAsViewed);

  const isAvailable = availability !== 'unavailable';
  const isEnabled = availability === 'enabled';
  const optOutReason = isAvailable && !isEnabled ? availability : undefined;
  // Turning the assistant on happens at the level it's switched off at, so the two levels need
  // different permissions: the workspace AI settings page vs. the project's AI settings page.
  const canOptIn =
    availability === 'disabled-by-workspace'
      ? !!account?.canModify
      : availability === 'disabled-by-project' && canChangeProjectSettings;

  const { data: lastViewedTurn } = useUserMetadata<string>(CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY, {
    enabled: isEnabled,
  });

  const { history, inProgress, isLoading, refetch } = useBuildAIConversationHistory(
    { ciConfigSlug: ciConfigSlug ?? '' },
    { enabled: isEnabled && !!ciConfigSlug },
  );

  const lastViewedTurnId = typeof lastViewedTurn === 'string' ? lastViewedTurn.split('#')[1] : undefined;
  const lastHistoryTurnId = history[history.length - 1]?.turnId;
  const hasNewReply =
    !hasBeenOpened && !!lastHistoryTurnId && !!lastViewedTurnId && lastHistoryTurnId !== lastViewedTurnId;

  return {
    isAvailable,
    isEnabled,
    optOutReason,
    canOptIn,
    ciConfigSlug,
    history,
    inProgress,
    isLoading,
    refetch,
    hasNewReply,
    markAsViewed,
  };
};

export default useCIConfigExpertConversation;
